import { useState, useEffect, useMemo } from 'react';
import type { DependencyGraph, ForceGraphData, ForceNode, ForceLink } from '../types/graph';
import { GRAPH_CONFIG } from '../types/graph';

export function useGraphData() {
  const [rawData, setRawData] = useState<DependencyGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch('/dependency-graph.json')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load graph data');
        return res.json();
      })
      .then((data: DependencyGraph) => {
        setRawData(data);
        // Initialize with all services selected
        setSelectedServices(new Set(Object.keys(data.statistics.nodesByService)));
        setSelectedLanguages(new Set(Object.keys(data.statistics.nodesByLanguage)));
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Transform to force graph format with filters applied
  const graphData: ForceGraphData = useMemo(() => {
    if (!rawData) return { nodes: [], links: [] };

    // Filter nodes
    const filteredNodes = rawData.nodes.filter(node => {
      const serviceMatch = selectedServices.has(node.service);
      const languageMatch = selectedLanguages.has(node.language);
      const searchMatch = searchQuery === '' ||
        node.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        node.id.toLowerCase().includes(searchQuery.toLowerCase());

      return serviceMatch && languageMatch && searchMatch;
    });

    const nodeIds = new Set(filteredNodes.map(n => n.id));

    // Convert nodes
    const nodes: ForceNode[] = filteredNodes.map(node => ({
      id: node.id,
      name: node.name,
      service: node.service,
      language: node.language,
      type: node.type,
      val: GRAPH_CONFIG.nodeValFile,
      color: GRAPH_CONFIG.serviceColors[node.service] || '#666666'
    }));

    // Filter edges - only include if both source and target are in filtered nodes
    const links: ForceLink[] = rawData.edges
      .filter(edge => nodeIds.has(edge.source) && nodeIds.has(edge.target))
      .map(edge => ({
        source: edge.source,
        target: edge.target,
        type: edge.type,
        color: GRAPH_CONFIG.edgeColors[edge.type] || '#999999',
        width: edge.type === 'http-call' ? GRAPH_CONFIG.edgeWidthHttp : GRAPH_CONFIG.edgeWidthDefault
      }));

    return { nodes, links };
  }, [rawData, selectedServices, selectedLanguages, searchQuery]);

  const availableServices = useMemo(() =>
    rawData ? Object.keys(rawData.statistics.nodesByService) : [],
    [rawData]
  );

  const availableLanguages = useMemo(() =>
    rawData ? Object.keys(rawData.statistics.nodesByLanguage) : [],
    [rawData]
  );

  return {
    graphData,
    rawData,
    loading,
    error,
    filters: {
      selectedServices,
      selectedLanguages,
      searchQuery,
      setSelectedServices,
      setSelectedLanguages,
      setSearchQuery
    },
    availableServices,
    availableLanguages
  };
}
